/** The queues ("Needs you") and the links into People, shared by Today, the sidebar and the palette. Pure, so the tests can pin them. */
import type { DeskPipeline, DeskPipelineStage } from "../../api/desk.types";

/** People view ids: each is registered in screens/people/views.ts. */
export const VIEW = {
  active: "active",
  lapsed: "lapsed",
  lapsed65: "lapsed-65",
  lapsed120: "lapsed-120",
  suspended: "suspended",
  atRisk: "at-risk",
  arrivals: "arrivals",
  licences: "licences",
  paymentIssues: "payment-issues",
  licenceChanges: "licence-changes",
  held: "held",
} as const;

export const peopleHref = (view: string): string => `/people?view=${encodeURIComponent(view)}`;

/** The people whose licence expires within 90 days: Licences, filtered by its first count (#635). */
export const LICENCES_WITHIN_90 = "/people?view=licences&licenseState=expiring90";

export function stage(pipeline: DeskPipeline | undefined, key: DeskPipelineStage["key"]): DeskPipelineStage | undefined {
  return pipeline?.stages.find((s) => s.key === key);
}

/** The grace stage's two display windows: within 65 days, and past 65 days (CE21 suspends at 120). */
export function graceWindows(pipeline: DeskPipeline | undefined) {
  const windows = stage(pipeline, "grace")?.windows ?? [];
  return {
    within65: windows.find((w) => w.key === "65"),
    past65: windows.find((w) => w.key === "120"),
  };
}

export type QueueKey = "payment" | "lapsed65" | "lapsed120" | "licences" | "held";

export interface QueueRow {
  key: QueueKey;
  reason: string;
  /** People who need action: open and resurfaced cases, not the ones already reviewed. */
  count: number;
  /** The queue's size, reviewed cases included. */
  total: number;
  view: string;
  /** Where the basis tooltip says the number comes from. */
  basis: string;
}

export interface QueueInputs {
  pipeline: DeskPipeline | undefined;
  licenceChanges: { toDo: number; done: number } | undefined;
  held: number | undefined;
}

function open(s: { count: number | null; needs_action_count?: number } | undefined): { count: number; total: number } {
  const total = s?.count ?? 0;
  return { count: s?.needs_action_count ?? total, total };
}

function reviewedNote(r: { count: number; total: number }): string {
  return r.total === r.count ? `${r.total} in the queue.` : `${r.count} still to do, ${r.total - r.count} already reviewed.`;
}

/** Every queue that has loaded, in the order a call list is worked, empty ones included: the sidebar lists them all so a row never moves. */
export function queueList({ pipeline, licenceChanges, held }: QueueInputs): QueueRow[] {
  const { within65, past65 } = graceWindows(pipeline);
  const payment = open(stage(pipeline, "payment_issues"));
  const w65 = open(within65);
  const w120 = open(past65);
  const rows: QueueRow[] = [
    {
      key: "payment",
      reason: "Payment problems",
      ...payment,
      view: VIEW.paymentIssues,
      basis: `Payments due or failed in the mirror. ${reviewedNote(payment)}`,
    },
    {
      key: "lapsed65",
      reason: "Lapsed within 65 days",
      ...w65,
      view: VIEW.lapsed65,
      basis: `Lapsed members still in the 65-day window. ${reviewedNote(w65)}`,
    },
    {
      key: "lapsed120",
      reason: "Lapsed 66–120 days",
      ...w120,
      view: VIEW.lapsed120,
      basis: `Lapsed past 65 days, before the mirror shows them suspended at 120. ${reviewedNote(w120)}`,
    },
  ];
  if (licenceChanges) {
    rows.push({
      key: "licences",
      reason: "Licence changes",
      count: licenceChanges.toDo,
      total: licenceChanges.toDo + licenceChanges.done,
      view: VIEW.licenceChanges,
      basis: `Licence dates that differ from the state register. ${licenceChanges.done} already done.`,
    });
  }
  if (held !== undefined) {
    rows.push({
      key: "held",
      reason: "Held for review",
      count: held,
      total: held,
      view: VIEW.held,
      basis: "Held back from the next wave until someone checks the match.",
    });
  }
  return rows;
}

/** Needs you: the queues with someone in them. Empty queues are left out. */
export function queueRows(inputs: QueueInputs): QueueRow[] {
  return queueList(inputs).filter((r) => r.count > 0);
}
